import { IPRights, Organization, Template } from "@xapp/ovai-lib";
import { InteractionModel as AlexaInteractionModel, SkillManifest as AlexaSkill } from "@xapp/stentor-alexa-lib";
import { GetGraphReturn } from "@xapp/stentor-api-client/dist/api/apps/appId/intents/graph/Graph";
import { Platform as ThirdPartyPlatforms } from "@xapp/stentor-api-client/dist/api/apps/appId/skill/Platform";
import { GetTokenReturn } from "@xapp/stentor-api-client/dist/api/cms/token/appId/AppId";
import { DeletePendingUserProps } from "@xapp/stentor-api-client/dist/api/orgs/orgId/users/pending/email/inviteId/InviteId";
import {
    FetchHTTPClient,
    Headers,
    PathParameters,
    Processor
} from "@xapp/stentor-api-client/dist/services/rest/fetch/FetchHTTPClient";
import {
    AddStripeAccount,
    AddUserOptions,
    AddUserToOrgReturn,
    AppId,
    CreateMultipleEntitiesProps,
    CreateMultipleEntitiesReturn,
    CreateSingleEntityProps,
    CreateSingleEntityReturn,
    CreateTokenReturn,
    DeleteAppReturn,
    DeleteEntityReturn,
    DeleteOrgReturn,
    EntityKey,
    ExportProps,
    ExportReturn,
    FileType,
    generateClient,
    GetAllEntitiesReturn,
    GetAllEntityProps,
    GetAppsParams,
    GetAppStatsReturn,
    GetAvailableAppsReturn,
    GetCouponProps,
    GetCouponReturn,
    GetOrgAccountSummaryReturn,
    GetSingleEntityReturn,
    GetSmapiTokenProps,
    GetSmapiTokenResponse,
    GetStentorEventsProps,
    GetStentorEventsReturn,
    GetUserAnalyticsPropsV3 as GetUserAnalyticsProps,
    GetUsersAnalyticsV3Return as GetUsersAnalyticsReturn,
    GetUsersOfOrgReturn,
    GetUsersOptions,
    IntentId,
    OrganizationId,
    Platform as IPRightsPlatform,
    RemoveUserOpts,
    RequestError,
    SearchAppsParams,
    SearchAppsReturn,
    ServiceOrder,
    SmapiVendorAccount,
    StentorClient,
    Stripe,
    SubmitAppReturn,
    SubscriptionType,
    TransferAppToOrgReturn,
    UpdateApp,
    UpdateAppIntentProps,
    UpdateAppIntentReturn,
    UpdateEntityProps,
    UpdateEntityReturn,
    UpdateIntent,
    UpdateOrg,
    UpdateTemplateResult,
    UpdateUserRolesOptions,
    UploadCustomIntroReturn,
    UploadIconContentTypes,
    UploadIconReturn,
    UploadIPRightsReturn,
    UploadOrgIconReturn,
    UploadTemplateResult,
    UploadThirdPartyCredentialsReturn,
    UploadThirdPartyProps,
    UserId,
    UserWithPolicy,
    Vendor
} from "@xapp/stentor-api-client/dist/StentorClient";
import { TokenId } from "@xapp/stentor-api-client/dist/utils/StentorUtils";
import { Handler, Intent } from "generated/graphql";
import { ClientApp } from "models";
import { pruneEmpty } from "stentor-utils";
import { autoBind } from "utils/Utils";
import { getApiUrl } from "utils/getApiUrl";
import { addParameters } from "./processors/AddParameters";
import { addToken, FullToken } from "./processors/AddToken";
import { removeUndefined } from "./processors/RemoveUndefined";

export {
    AddStripeAccount,
    AddUserToOrgReturn,
    AdminAppStats,
    AppId,
    AppsAppIdTransfer,
    AppStats,
    CmsTokenObject,
    CreateCmsToken,
    CreateMultipleEntitiesProps,
    CreateMultipleEntitiesReturn,
    CreateSingleEntityProps,
    CreateSingleEntityReturn,
    CreateTokenReturn,
    DeleteEntityReturn,
    EntityKey,
    ESEvent,
    ExportPlatform,
    ExportProps,
    ExportReturn,
    FileType,
    FullStats,
    GetAllEntitiesReturn,
    GetAppsParams,
    GetAvailableAppsReturn as AvailableAppsSummary,
    GetOrgAccountSummaryReturn,
    GetSingleEntityKey,
    GetSingleEntityReturn,
    GetSmapiTokenProps,
    GetSmapiTokenResponse,
    GetStentorEventsProps,
    GetStentorEventsReturn,
    GetUserAnalyticsPropsV3 as GetUserAnalyticsProps,
    GetUsersAnalyticsV3Return as GetUsersAnalyticsReturn,
    GetUsersOfOrgReturn,
    IntentId,
    IntervalV3 as Interval,
    OrganizationId,
    OrgStats,
    PendingUser as OrgPendingUser,
    RequestError,
    SearchAppsParams,
    SearchAppsReturn,
    ServiceOrder,
    SmapiVendorAccount,
    Stripe,
    SubmitAppReturn,
    SubscriptionType,
    UpdateEntityProps,
    UpdateEntityReturn,
    UploadBannerContentTypes,
    UploadCustomIntroReturn,
    UploadIconContentTypes,
    UploadIconReturn,
    UploadIPRightsReturn,
    UploadTemplateResult,
    User as OrgUser,
    UserEventV3 as UserEvent,
    UserWithPolicy as OrgUserWithPolicy,
    Vendor
} from "@xapp/stentor-api-client";
export * from "@xapp/stentor-api-client/dist/models/Models";
export { WebSocketService } from "@xapp/stentor-api-client/dist/services/sockets/websocket/WebsocketService";
export {
    FailureReason,
    RunningState,
    StartedState,
    StoppedState,
    TestBasePayload,
    TestEventListener,
    TestPayload,
    TestResult,
    TestStoppedPayload,
    TestSuiteChangedPayload,
    TestSuiteEndedPayload,
    TestSuiteRunningPayload,
    TEST_FAIL,
    TEST_NOT_RUN, TEST_PASS,
    UtteranceTest
} from "@xapp/stentor-api-client/dist/sockets/test/TestEventListener";
export { getAppId, getIntentId, getOrgId } from "@xapp/stentor-api-client/dist/utils/StentorUtils";
export { AlexaInteractionModel, AlexaSkill, GetGraphReturn, TokenId };

/**
 * All audio contents supported.
 */
export type AudioContentType = "audio/mp3";
/**
 * All image contents supported.
 */
export type ImageContentType = "image/png";

/**
 * Combination of all content types that are currently supported.
 */
export type ContentType = AudioContentType | ImageContentType;

/**
 * Return value for Download IP Rights api endpoint.
 */
export interface DownloadIPRightsReturn {
    /**
     * Base64 encoded content of the item.
     */
    base64Content: string;
    /**
     * The content mime type of this item.
     */
    contentType: string;
}

export interface APICredentials {
    xappUserToken?: FullToken;
    askToken?: FullToken;
}

/**
 * @deprecated Deprecated in favor of GraphQL, only a few key uses are appropriate
 */
export class ApiGatewayApi {
    private readonly nuClient: StentorClient;

    constructor(creds: APICredentials) {
        autoBind(this);

        const pathProcessors: Processor<PathParameters>[] = [removeUndefined()];
        const headerProcessors: Processor<Headers>[] = [
            removeUndefined(),
            // @ts-ignore
            addParameters({
                "Accept": "application/json",
                "Content-Type": "application/json"
            }),
            addToken("x-xapp-usertoken", creds.xappUserToken),
            addToken("x-xapp-asktoken", creds.askToken)
        ];
        this.nuClient = generateClient(
            new FetchHTTPClient({
                basePath: getApiUrl(),
                headerProcessor: headerProcessors,
                pathProcessor: pathProcessors
            })
        );
    }

    acceptInvite(inviteId: string): Promise<void> {
        return resolveAndCatch(this.nuClient.acceptInvite(inviteId)).then(() => { });
    }

    addPaymentAccountToOrganization(
        oId: OrganizationId,
        paymentAccounts: AddStripeAccount | ServiceOrder,
        idempotencyKey?: string
    ): Promise<Organization> {
        if (isAddStripeAccountObject(paymentAccounts)) {
            return resolveAndCatch(this.nuClient.addStripeAccount(oId, paymentAccounts, idempotencyKey)).then(
                result => {
                    return result.organization;
                }
            );
        } else {
            return resolveAndCatch(this.nuClient.addServiceOrder(oId, paymentAccounts)).then(result => {
                return result.organization;
            });
        }
    }

    addPublishingAccountToOrganization(
        oId: OrganizationId,
        publisherAccounts: { smapi: SmapiVendorAccount }
    ): Promise<Organization> {
        return resolveAndCatch(this.nuClient.addSmapiVendorAccount(oId, publisherAccounts.smapi)).then(result => {
            return result.organization;
        });
    }

    addUserToOrganization(
        uId: { email?: string; userId?: string } | { email?: string; userId?: string }[],
        orgId: OrganizationId,
        options: AddUserOptions
    ): Promise<AddUserToOrgReturn> {
        return resolveAndCatch(this.nuClient.addUserToOrganization(uId, orgId, options));
    }

    completeVerification(): Promise<void> {
        return resolveAndCatch(this.nuClient.completeVerification());
    }

    createApp(app: ClientApp): Promise<ClientApp> {
        // @ts-ignore
        return resolveAndCatch(this.nuClient.createApp(app), "Error creating app").then(result => {
            // @ts-ignore
            return result.app;
        });
    }

    createEntity(app: AppId, props: CreateSingleEntityProps): Promise<CreateSingleEntityReturn>;
    createEntity(app: AppId, props: CreateMultipleEntitiesProps): Promise<CreateMultipleEntitiesReturn>;
    createEntity(
        app: AppId,
        props: CreateSingleEntityProps | CreateMultipleEntitiesProps
    ): Promise<CreateSingleEntityProps> | Promise<CreateMultipleEntitiesReturn> {
        return resolveAndCatch(this.nuClient.createEntity(app, props as CreateSingleEntityProps));
    }

    createOrgs(organization: Partial<Organization>): Promise<Organization> {
        return resolveAndCatch(this.nuClient.createOrg(organization)).then(result => {
            return result.organization;
        });
    }

    createCMSToken(appId: AppId): Promise<CreateTokenReturn> {
        return resolveAndCatch(this.nuClient.createToken(appId));
    }

    deleteApp(app: AppId): Promise<DeleteAppReturn> {
        return resolveAndCatch(this.nuClient.deleteApp(app));
    }

    deleteEntity(entity: EntityKey): Promise<DeleteEntityReturn> {
        return resolveAndCatch(this.nuClient.deleteEntity(entity));
    }

    deleteOrg(organization: OrganizationId): Promise<DeleteOrgReturn> {
        return resolveAndCatch(this.nuClient.deleteOrg(organization));
    }

    deletePendingUser(orgId: OrganizationId, props: DeletePendingUserProps): Promise<any> {
        return resolveAndCatch(this.nuClient.deletePendingUser(orgId, props));
    }

    deleteCMSToken(tokenId: TokenId): Promise<void> {
        return resolveAndCatch(this.nuClient.deleteToken(tokenId));
    }

    exportApp(id: AppId, props: ExportProps): Promise<ExportReturn> {
        return resolveAndCatch(this.nuClient.exportApp(id, props), "Error exporting skill").then(response => {
            return {
                content: response.content
            };
        });
    }

    getAdminStatsApps(): Promise<GetAppStatsReturn> {
        return resolveAndCatch(this.nuClient.getAdminAppStats());
    }

    getAppIntentsGraph(id: AppId): Promise<GetGraphReturn> {
        return resolveAndCatch(this.nuClient.getAppIntentsGraph(id), "Error retrieving App Intents Graph").then(
            response => {
                return {
                    connections: response.connections,
                    nodes: response.nodes
                };
            }
        );
    }

    getApps(queryParams?: OrganizationId | GetAppsParams): Promise<ClientApp[]> {
        let useParams: GetAppsParams;
        if (queryParams) {
            if (typeof queryParams === "string") {
                useParams = { organizationId: queryParams };
            } else {
                useParams = { ...queryParams };
            }
        }
        // @ts-ignore
        return resolveAndCatch(this.nuClient.getApps(useParams), "Error retrieving apps").then(result => {
            return result.apps;
        });
    }

    getApp(appId: AppId): Promise<ClientApp> {
        // @ts-ignore
        return resolveAndCatch(this.nuClient.getApp(appId), "Could not retrieve app").then(result => {
            return result.app;
        });
    }

    getAppHandlersAndIntents(appId: AppId): Promise<(Handler | Intent)[]> {
        return resolveAndCatch(this.nuClient.getAppIntents(appId), "Error retrieving intents").then(result => {
            return result.appIntents as Intent[];
        });
    }

    getAppIntentOrHandler(appId: AppId, intentId: IntentId): Promise<Handler | Intent> {
        return resolveAndCatch(this.nuClient.getAppIntent(appId, intentId), "Error retrieving intent").then(result => {
            return result.appIntent as Intent;
        });
    }

    getAppSkill(appId: AppId): Promise<AlexaSkill> {
        return resolveAndCatch(this.nuClient.getSkill(appId)).then(result => {
            return result.skill;
        });
    }

    getAppSkillInteractionModel(appId: AppId, locale?: string): Promise<AlexaInteractionModel> {
        return resolveAndCatch(this.nuClient.getSkillInteractionModel(appId, locale)).then(result => {
            return result.model;
        });
    }

    getAppUsersAnalytics(appId: AppId, props?: GetUserAnalyticsProps): Promise<GetUsersAnalyticsReturn> {
        return resolveAndCatch(this.nuClient.getUserAnalyticsV3(appId, props));
    }

    getAvailableApps(oId: OrganizationId, template: SubscriptionType): Promise<GetAvailableAppsReturn> {
        return resolveAndCatch(this.nuClient.getAvailableApps(oId, template));
    }

    getCMSTokens(appId: AppId): Promise<GetTokenReturn> {
        return resolveAndCatch(this.nuClient.getTokens(appId));
    }

    getCoupon(props: GetCouponProps): Promise<GetCouponReturn> {
        return resolveAndCatch(this.nuClient.getCoupon(props));
    }

    getIPRights(oId: OrganizationId, platform: keyof IPRights): Promise<DownloadIPRightsReturn> {
        return resolveAndCatch(this.nuClient.getIPRights(oId, platform)).then(result => ({
            base64Content: getBase64Content(result.content),
            contentType: getBase64ContentType(result.content)
        }));
    }

    getEntities(
        appId: AppId,
        props?: GetAllEntityProps
    ): Promise<GetAllEntitiesReturn> | Promise<GetSingleEntityReturn> {
        return resolveAndCatch(this.nuClient.getEntities(appId, props));
    }

    getOrg(orgId: OrganizationId): Promise<Organization> {
        return resolveAndCatch(this.nuClient.getOrg(orgId)).then(result => {
            return result.organization;
        });
    }

    getOrgs<K extends keyof Organization>(projection?: K[]): Promise<Pick<Organization, K>[]> {
        return resolveAndCatch(this.nuClient.getOrgs(projection), "Error retrieving orgs").then(result => {
            return result.organizations || [];
        });
    }

    getOrgUserAnalytics(
        organizationId: OrganizationId,
        props?: GetUserAnalyticsProps
    ): Promise<GetUsersAnalyticsReturn> {
        return resolveAndCatch(this.nuClient.getOrgUserAnalyticsV3(organizationId, props));
    }

    getSmapiToken(props: GetSmapiTokenProps): Promise<GetSmapiTokenResponse> {
        return resolveAndCatch(this.nuClient.getSmapiToken(props));
    }

    getPaymentAccountSummary(oId: OrganizationId): Promise<GetOrgAccountSummaryReturn> {
        return resolveAndCatch(this.nuClient.getOrgAccountSummary(oId));
    }

    getStentorEvents(props: GetStentorEventsProps): Promise<GetStentorEventsReturn> {
        return resolveAndCatch(this.nuClient.getStentorEvents(props), "Error retrieving events");
    }

    getUsersOfOrganization(orgId: OrganizationId, options: GetUsersOptions): Promise<GetUsersOfOrgReturn> {
        return resolveAndCatch(this.nuClient.getUsersOfOrganization(orgId, options));
    }

    getVendors(): Promise<Vendor[]> {
        return resolveAndCatch(this.nuClient.getVendors()).then(result => {
            return result.vendors;
        });
    }

    removeUserFromOrganization(org: OrganizationId, userId: UserId, opts?: RemoveUserOpts): Promise<void> {
        return resolveAndCatch(this.nuClient.removeUserFromOrganization(org, userId, opts));
    }

    resendVerificationEmail(): Promise<void> {
        return resolveAndCatch(this.nuClient.resendVerificationEmail());
    }

    searchApps(queryParams?: SearchAppsParams): Promise<SearchAppsReturn> {
        return resolveAndCatch(this.nuClient.searchApps(queryParams), "Error searching for apps.");
    }

    submitApp(appId: AppId, stripe: Stripe, notes?: string): Promise<SubmitAppReturn> {
        return resolveAndCatch(this.nuClient.submitApp(appId, stripe, notes));
    }

    submitAppSkill(appId: AppId, vendorId: string): Promise<AlexaSkill> {
        return resolveAndCatch(this.nuClient.submitSkill(appId, vendorId)).then(result => {
            return result.skill;
        });
    }

    submitAppSkillInteractionModel(appId: AppId): Promise<AlexaInteractionModel> {
        return resolveAndCatch(this.nuClient.createSkillInteractionModel(appId)).then(result => {
            return result.alexa.localModel["en-US"];
        });
    }

    transferApp(appId: string, orgId: string): Promise<TransferAppToOrgReturn> {
        return resolveAndCatch(this.nuClient.transferAppToOrg(appId, orgId), `Error transferring ${appId} to ${orgId}`);
    }

    updateApp(app: AppId, updateObj: UpdateApp): Promise<ClientApp> {
        // @ts-ignore
        return resolveAndCatch(this.nuClient.updateApp(app, updateObj), "Error updating app").then(
            result => {
                return result.app;
            }
        );
    }

    updateIntent(
        appIntent: { appId: string; intentId: string },
        updateObj: UpdateIntent,
        props?: UpdateAppIntentProps
    ): Promise<UpdateAppIntentReturn> {
        return resolveAndCatch(
            this.nuClient.updateAppIntent(appIntent.appId, appIntent.intentId, updateObj, props),
            "Error updating intent"
        );
    }

    updateEntity(entity: EntityKey, props: UpdateEntityProps): Promise<UpdateEntityReturn> {
        return resolveAndCatch(this.nuClient.updateEntity(entity, props));
    }

    updateOrg(organization: OrganizationId, updateObj: UpdateOrg): Promise<Organization> {
        return resolveAndCatch(this.nuClient.updateOrg(organization, pruneEmpty(updateObj))).then(result => {
            return result.organization;
        });
    }

    updateUserRolesInOrganization(
        userId: UserId,
        org: OrganizationId,
        opts: UpdateUserRolesOptions
    ): Promise<UserWithPolicy> {
        return resolveAndCatch(this.nuClient.updateUserRolesInOrganization(userId, org, opts)).then(result => {
            return result.user;
        });
    }

    updateTemplate(template: Template): Promise<UpdateTemplateResult> {
        return resolveAndCatch(this.nuClient.updateTemplate(template));
    }

    uploadAndAddAudio(
        organizationId: OrganizationId,
        appId: AppId,
        intentId: IntentId,
        audio: FileType,
        name: string,
        contentType: AudioContentType,
        index?: number
    ): Promise<UploadCustomIntroReturn> {
        return resolveAndCatch(
            this.nuClient.encodeAudio(organizationId, appId, intentId, audio, {
                contentType,
                index,
                name
            })
        );
    }

    uploadAndEncodeAudio(
        organizationId: OrganizationId,
        appId: AppId,
        intentId: IntentId,
        audio: FileType,
        name: string,
        contentType: AudioContentType,
        index?: number
    ): Promise<UploadCustomIntroReturn> {
        return resolveAndCatch(
            this.nuClient.encodeAudio(organizationId, appId, intentId, audio, {
                contentType,
                index,
                name
            })
        );
    }

    uploadAsset(
        oId: OrganizationId,
        blob: FileType,
        name: string,
        contentType: ContentType
    ): Promise<{ location: string }> {
        if (contentType.startsWith("image")) {
            return resolveAndCatch(
                this.nuClient.uploadImage(oId, blob, {
                    contentType,
                    name
                })
            );
        } else if (contentType.startsWith("audio")) {
            return resolveAndCatch(
                this.nuClient.uploadAudio(oId, blob, {
                    contentType,
                    name
                })
            );
        } else {
            return Promise.reject(new Error("Media type " + contentType + " currently not supported."));
        }
    }

    uploadBanner(appId: AppId, icon: Blob, imageType: UploadIconContentTypes): Promise<UploadIconReturn> {
        return resolveAndCatch(
            this.nuClient.uploadBanner(appId, icon, {
                contentType: imageType,
                noUpdate: true
            })
        );
    }

    uploadIcon(appId: AppId, icon: Blob, imageType: UploadIconContentTypes, noUpdate: boolean = true): Promise<UploadIconReturn> {
        return resolveAndCatch(
            this.nuClient.uploadIcon(appId, icon, {
                contentType: imageType,
                noUpdate
            })
        );
    }

    uploadIPRights(
        oId: OrganizationId,
        ipRights: FileType,
        platform: IPRightsPlatform = "alexa"
    ): Promise<UploadIPRightsReturn> {
        return resolveAndCatch(this.nuClient.uploadIPRights(oId, platform, ipRights));
    }

    uploadOrgIcon(orgId: OrganizationId, icon: Blob, imageType: UploadIconContentTypes, noUpdate: boolean = true): Promise<UploadOrgIconReturn> {
        return resolveAndCatch(
            this.nuClient.uploadOrgIcon(orgId, icon, {
                contentType: imageType,
                noUpdate
            })
        );
    }

    uploadTemplate(template: Template): Promise<UploadTemplateResult> {
        return resolveAndCatch(this.nuClient.uploadTemplate(template));
    }

    uploadThirdPartyCredentials(appId: AppId, platform: ThirdPartyPlatforms, props: UploadThirdPartyProps): Promise<UploadThirdPartyCredentialsReturn> {
        return resolveAndCatch(this.nuClient.uploadThirdPartyCredentials(appId, platform, props));
    }
}

function isAddStripeAccountObject(obj: ServiceOrder | AddStripeAccount): obj is AddStripeAccount {
    return (obj as AddStripeAccount).token !== undefined && (obj as AddStripeAccount).email !== undefined;
}

/**
 * Retrieves the contentType found in the uri of a base64 encoded doc.
 * This can return undefined if the data was base64 encoded without a content type.
 * @param base64EncodedUri The base64 encoding to search through.
 */
function getBase64ContentType(base64EncodedUri = "") {
    const locationOfData = base64EncodedUri.indexOf("data:");
    const endOfData = base64EncodedUri.indexOf(";", locationOfData);
    return locationOfData >= 0 ? base64EncodedUri.substring(locationOfData + 5, endOfData) : undefined;
}

function getBase64Content(base64EncodedUri = "") {
    return base64EncodedUri.replace(/data:\w+\/\w+;base64,/, "");
}

/**
 * Will turn the promise in to a Promise promise and then catch the error with the `catchError` method.
 * @param promise The promise to convert.
 * @param prefix Any error message that should be prefixed to the base error message.
 */
function resolveAndCatch<T>(promise: Promise<T>, prefix?: string): Promise<T> {
    return Promise.resolve(promise).catch(e => {
        throw convertError(e, prefix);
    });
}

/**
 * This captures the error then makes it more client facing.
 * @param error Status code of the error.
 * @param prefix The prefix to add to the error.  If placed, it will be "<prefix> - Message: <realMessage>; Code: <realCode>"
 */
function convertError(error: Error | RequestError, prefix = "") {
    const errorMessage: string = (error as RequestError).statusMessage || error.message;
    const errorCode: number = (error as RequestError).statusCode || -1;
    return new Error(`${prefix} - Message: ${errorMessage}; Code: ${errorCode}`);
}
